import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Loader } from '../../../Loader';
import { MobileCalendarEventItem } from './MobileCalendarEventItem.component';
import { useMobileCalendarEventsList } from './MobileCalendarEventsList.hook';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { Appointment, ICSEvent } from '@uplift/api';
import { EventItemVariant } from '../../EventItem';
import { UserStatus } from '@uplift/types';


// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()(theme => ({
  root: {
    paddingTop: theme.spacing(1),
    height: '100%',
  },
  empty: {
    padding: theme.spacing(1),
    paddingTop: theme.spacing(3),
    height: '100%',
    fontStyle: 'italic',
    userSelect: 'none',
  }
}));


export type MobileCalendarEventsListProps = {
  today: string;
  date: string;
  onICSEventClick?: (event: ICSEvent) => void;
  onEventClick?: (appointment: Appointment) => void;
};


export const MobileCalendarEventsList: React.FC<MobileCalendarEventsListProps> = (props) => {
  const { classes } = useStyles();
  const timezone = useTimezone();
  const { events, isEventsLoading } = useMobileCalendarEventsList(props.date, timezone);
  const isPastDate = React.useMemo(() => {
    const date = CalendarDateService.date(props.date, DateFormats.ISO_DATE);
    const today = CalendarDateService.date(props.today, DateFormats.ISO_DATE);

    return date.isBefore(today, 'date');
  }, [ props.date, props.today ]);
  const loading = isEventsLoading;

  if (loading) {
    return (
      <Box className={classes.root}>
        <Loader color="primary" />
      </Box>
    );
  }

  if (!events || events?.length === 0) {
    if (isPastDate) {
      return (
        <Box className={classes.empty}>
          <Typography color="textSecondary" align="center">No session held</Typography>
        </Box>
      );
    }
    return (
      <Box className={classes.empty}>
        <Typography color="textSecondary" align="center">Schedule is open</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {events.map((event, index) => {
        if (event.type === 'ics') {
          return (
            <>
              {index > 0 && <Divider />}
              <Box key={event.event.id} pl={event.elevation * 2}>
                <MobileCalendarEventItem
                  variant="ics"
                  start={CalendarDateService.date(event.event.fromDatetime).tz(timezone)}
                  end={CalendarDateService.date(event.event.toDatetime).tz(timezone)}
                  title={event.event.title}
                  allDay={event.event.allDay}
                  onClick={() => props.onICSEventClick?.(event.event)}
                />
              </Box>
            </>
          );
        } else if (event.type === 'appointment') {
          const { appointment, patient, isICSConflict } = event;
          let variant: EventItemVariant = 'appointment';

          if (patient.status !== UserStatus.regular) {
            variant = 'appointmentWarning';
          } else if (isICSConflict) {
            variant = 'appointmentOverlapWarning';
          }

          return (
            <>
              {index > 0 && <Divider />}
              <Box key={appointment.id} pl={event.elevation * 2}>
                <MobileCalendarEventItem
                  variant={variant}
                  start={CalendarDateService.date(appointment.fromDatetime).tz(timezone)}
                  end={CalendarDateService.date(appointment.toDatetime).tz(timezone)}
                  title={`${patient?.preferred_name || patient?.first_name} ${patient.last_name}`}
                  onClick={() => props.onEventClick?.(appointment)}
                />
              </Box>
            </>
          );
        }

        return null;
      })}
    </Box>
  );
};
